import { useNotifications } from '@toolpad/core';
import { DialogsProvider } from '@toolpad/core/useDialogs';
import { signOut, User } from 'firebase/auth';
import * as React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useCheckDayliteAccess } from 'src/hooks/useCheckDayliteAccess';
import { onAuthStateChanged } from '../firebase/auth';
import { auth } from '../firebase/firebaseConfig';
import SessionContext, { type Session } from '../SessionContext';

// Hook to manage session state that can be used by App component
export function useAppSession() {
  const { checkDayliteAccess } = useCheckDayliteAccess();
  const notifications = useNotifications();
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true); // Initialize loading to true

  const sessionContextValue = React.useMemo(
    () => ({
      session,
      setSession,
      loading
    }),
    [session, loading]
  );

  React.useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(async (user: User | null) => {
      if (user) {
        // Check if email is verified
        const isEmailVerified = user.emailVerified;

        // Check if user has access in daylite
        const accessResult = await checkDayliteAccess(user);
        const { success, accountType, error, firstName, lastName, dayliteId } = accessResult;

        if (!success) {
          // Don't sign out, just clear session and show error
          setSession(null); 
          setLoading(false);
          notifications.show(`Daylite access check failed: ${error || 'Unknown error'}`, {
            severity: 'error',
            autoHideDuration: 3000
          });
          // No need to proceed further if access check failed
          return; 
        }

        // Proceed only if Daylite access check was successful
        const sessionData = {
          user: {
            name: user.displayName || `${firstName} ${lastName}` || '',
            email: user.email || '',
            image: user.photoURL || ''
          },
          accountType,
          emailVerified: isEmailVerified,
          firebaseId: user.uid,
          dayliteId: dayliteId
        };

        // Store the user and access permission in session
        setSession(sessionData);
        setLoading(false);
      } else {
        setSession(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return sessionContextValue;
}

// AppContent component with session management logic
const AppContent = () => {
  const location = useLocation();
  const sessionContextValue = useAppSession();
  const { session } = sessionContextValue;

  // Check if we need to redirect to verification page
  const shouldShowVerification =
    session &&
    !session.emailVerified &&
    location.pathname !== '/no-access/verify' &&
    !location.pathname.startsWith('/sign');

  return (
    <SessionContext.Provider value={sessionContextValue}>
      {shouldShowVerification ? (
        <Navigate to="/no-access/verify" replace />
      ) : (
        <>
          <DialogsProvider>
            <Outlet />
          </DialogsProvider>
        </>
      )}
    </SessionContext.Provider>
  );
};

export default AppContent;
